import { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import PersonaAvatar from '../../components/PersonaAvatar'
import PersonaMetricsGrid from '../../components/PersonaMetricsGrid'
import BenchmarkTab from '../../components/BenchmarkTab'
import personaXpData from '../../data/persona-xp.json'

const QUEST_TYPES = [
  { key: 'research', label: 'Research', color: '#6FB8D6' },
  { key: 'build', label: 'Build', color: '#5AC884' },
  { key: 'analyze', label: 'Analyze', color: '#B265D6' },
  { key: 'review', label: 'Review', color: '#E8935A' },
  { key: 'creative', label: 'Creative', color: '#D65A93' },
  { key: 'social', label: 'Social', color: '#6FB8D6' },
] as const

const STATUS_COLORS: Record<string, string> = {
  proposed: '#6B7280',
  discussing: '#E6C85A',
  active: '#6FB8D6',
  completed: '#5AC884',
  evaluated: '#B265D6',
  awarded: '#E8935A',
  guided: '#D29922',
  closed: '#4B5563',
}

const STATUS_STEPS = ['proposed', 'discussing', 'active', 'completed', 'evaluated', 'awarded']

interface Quest {
  id: string
  title: string
  type: string
  description: string
  deliverable: string
  persona: string
  questGiver: string
  status: string
  difficulty: string
  qualityScore?: number
  xpAwarded?: Record<string, number>
  createdAt: string
  completedAt?: string
}

function QuestTypeBadge({ type }: { type: string }) {
  const keys = type.includes('+') ? type.split('+') : [type]
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {keys.map(key => {
        const t = QUEST_TYPES.find(q => q.key === key.trim())
        return (
          <span key={key} style={{
            fontSize: 9, padding: '2px 8px', borderRadius: 10,
            background: `${t?.color || '#6B7280'}18`,
            color: t?.color || '#6B7280',
            fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
            border: `1px solid ${t?.color || '#6B7280'}30`,
          }}>
            {t?.label || key}
          </span>
        )
      })}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || '#6B7280'
  return (
    <span style={{
      fontSize: 9, padding: '2px 8px', borderRadius: 10,
      background: `${color}18`, color,
      fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
    }}>
      {status}
    </span>
  )
}

function ProgressTrack({ status }: { status: string }) {
  const idx = STATUS_STEPS.indexOf(status)
  return (
    <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      {STATUS_STEPS.map((step, i) => (
        <div key={step} style={{
          flex: 1, height: 3, borderRadius: 2,
          background: i <= idx ? (STATUS_COLORS[step] || '#6B7280') : 'var(--bg-elevated)',
          transition: 'background 0.3s',
        }} />
      ))}
    </div>
  )
}

function formatTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(diff / 3600000)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(diff / 86400000)
  return `${days}d ago`
}

function QuestCard({ quest: q }: { quest: Quest }) {
  const navigate = useNavigate()
  return (
    <div
      onClick={() => navigate(`/evolve/quests/${q.id}`)}
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 12, padding: 16, cursor: 'pointer',
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = STATUS_COLORS[q.status] || 'var(--border)'
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = 'var(--border)'
        e.currentTarget.style.boxShadow = 'none'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{q.title}</span>
            <QuestTypeBadge type={q.type} />
            <StatusBadge status={q.status} />
          </div>
          <ProgressTrack status={q.status} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <PersonaAvatar name={q.persona} size={24} />
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {q.persona.charAt(0).toUpperCase() + q.persona.slice(1)}
            </span>
          </div>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
            {formatTime(q.createdAt)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>→</span>
        </div>
      </div>
    </div>
  )
}

const LEVEL_COLORS: Record<number, string> = {
  0: '#6B7280', 1: '#6FB8D6', 2: '#E6C85A', 3: '#E8935A',
}
const LEVEL_NAMES: Record<number, string> = {
  0: 'Newcomer', 1: 'Citizen', 2: 'Contributor', 3: 'Steward',
}
const THRESHOLDS = personaXpData.thresholds
const THRESHOLD_KEYS = ['citizen', 'contributor', 'steward'] as const

function getEvolvePct(p: any): { pct: number; blocking: string; current: number; required: number } {
  if (p.level >= 3) return { pct: 100, blocking: '', current: 0, required: 0 }
  const next = THRESHOLDS[THRESHOLD_KEYS[p.level]] as any
  if (!next) return { pct: 0, blocking: 'xp', current: p.total || 0, required: 0 }
  const dims = ['com','soc','cre','wis','srv','idn']
  const totalPct = next.total_min ? Math.min(100, ((p.total || 0) / next.total_min) * 100) : 100
  const dimsPassing = dims.filter(k => (p[k] || 0) >= (next.dims_above || 0)).length
  const dimsPct = next.dims_required ? Math.min(100, (dimsPassing / next.dims_required) * 100) : 100
  let depthPct = 100
  if (next.depth_min) {
    const deepCount = dims.filter(k => (p[k] || 0) >= next.depth_min).length
    const needed = next.depth_count || 1
    depthPct = Math.min(100, (deepCount / needed) * 100)
  }
  const overallPct = Math.min(totalPct, dimsPct, depthPct)
  let blocking = '', current = 0, required = 0
  if (totalPct <= dimsPct && totalPct <= depthPct) {
    blocking = 'total XP'; current = p.total || 0; required = next.total_min || 0
  } else if (dimsPct <= depthPct) {
    const lowest = dims.reduce((a, b) => ((p[a] || 0) < (p[b] || 0) ? a : b))
    blocking = lowest; current = p[lowest] || 0; required = next.dims_above || 0
  } else {
    const lowest = dims.reduce((a, b) => ((p[a] || 0) < (p[b] || 0) ? a : b))
    blocking = lowest; current = p[lowest] || 0; required = next.depth_min || 0
  }
  return { pct: Math.round(overallPct), blocking, current, required }
}

const DIM_COLORS: Record<string, string> = {
  com: '#E8935A', soc: '#6FB8D6', cre: '#B265D6',
  wis: '#E6C85A', srv: '#5AC884', idn: '#D65A93',
}
const DIM_LABELS: Record<string, string> = {
  com: 'COM', soc: 'SOC', cre: 'CRE', wis: 'WIS', srv: 'SRV', idn: 'IDN',
}

interface FeedEvent {
  ts: string
  persona: string
  activity: string
  dims: { key: string; pts: number }[]
}

function generateFeedFromStatic(): FeedEvent[] {
  const events: FeedEvent[] = []
  const now = new Date()
  Object.entries(personaXpData.personas).forEach(([name, data]: [string, any]) => {
    if (data.ceremony && data.ceremony !== 'None') {
      const parts = data.ceremony.split('+').map((s: string) => s.trim())
      for (const part of parts) {
        const d = new Date(now.getTime() - Math.random() * 30 * 86400000)
        if (part.toLowerCase().includes('awakening')) {
          events.push({ ts: d.toISOString(), persona: name, activity: `${part}`, dims: [{ key: 'idn', pts: 40 + Math.floor(Math.random() * 60) }, { key: 'wis', pts: 10 + Math.floor(Math.random() * 30) }] })
        } else if (part.toLowerCase().includes('review')) {
          events.push({ ts: d.toISOString(), persona: name, activity: 'Review Ceremony', dims: [{ key: 'wis', pts: 20 + Math.floor(Math.random() * 30) }, { key: 'idn', pts: 15 }] })
        } else {
          events.push({ ts: d.toISOString(), persona: name, activity: part, dims: [{ key: 'idn', pts: 30 + Math.floor(Math.random() * 50) }] })
        }
      }
    }
    if (data.com > 100) {
      events.push({ ts: new Date(now.getTime() - Math.random() * 7 * 86400000).toISOString(), persona: name, activity: 'Task completions this week', dims: [{ key: 'com', pts: Math.floor(data.com * 0.1) }] })
    }
    if (data.soc > 50) {
      events.push({ ts: new Date(now.getTime() - Math.random() * 7 * 86400000).toISOString(), persona: name, activity: 'Community conversations', dims: [{ key: 'soc', pts: Math.floor(data.soc * 0.05) }] })
    }
    if (data.cre > 50) {
      events.push({ ts: new Date(now.getTime() - Math.random() * 14 * 86400000).toISOString(), persona: name, activity: 'Creative output posted', dims: [{ key: 'cre', pts: Math.floor(data.cre * 0.08) }] })
    }
    if (data.srv > 50) {
      events.push({ ts: new Date(now.getTime() - Math.random() * 14 * 86400000).toISOString(), persona: name, activity: 'Service contribution', dims: [{ key: 'srv', pts: Math.floor(data.srv * 0.06) }] })
    }
  })
  events.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
  return events
}

function GrowthMetricsView() {
  const [view, setView] = useState<'cards' | 'feed'>('cards')
  const [feedFilter, setFeedFilter] = useState<string>('all')

  const { data: apiFeed } = useQuery<FeedEvent[]>({
    queryKey: ['benchmark-feed'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/benchmark/feed?limit=2000')
        if (!res.ok) return null
        const raw = await res.json()
        const events = Array.isArray(raw) ? raw : (raw.events || raw.feed || [])
        const knownPersonas = new Set(Object.keys(personaXpData.personas))
        return events
          .filter((e: any) => knownPersonas.has(e.persona))
          .map((e: any) => ({
            ts: e.timestamp || e.ts,
            persona: e.persona,
            activity: e.activity || e.description || e.event,
            dims: e.dimensions
              ? Object.entries(e.dimensions).map(([k, v]) => ({ key: k, pts: v as number }))
              : (e.dims || []),
          })).sort((a: FeedEvent, b: FeedEvent) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
      } catch { return null }
    },
    staleTime: 30_000,
  })

  const allEvents: FeedEvent[] = useMemo(() => {
    if (apiFeed && apiFeed.length > 0) return apiFeed
    const feed = (personaXpData as any).community_feed
    if (Array.isArray(feed) && feed.length > 0) {
      return feed.map((e: any) => ({
        ts: e.timestamp,
        persona: e.persona,
        activity: e.activity,
        dims: Object.entries(e.dimensions || {}).map(([k, v]) => ({ key: k, pts: v as number })),
      })).sort((a: FeedEvent, b: FeedEvent) => new Date(b.ts).getTime() - new Date(a.ts).getTime())
    }
    return generateFeedFromStatic()
  }, [apiFeed])

  const allPersonas = useMemo(() => [...new Set(allEvents.map(e => e.persona))].sort(), [allEvents])
  const filteredEvents = feedFilter === 'all' ? allEvents : allEvents.filter(e => e.persona === feedFilter)

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <button
          onClick={() => setView('cards')}
          style={{
            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            background: view === 'cards' ? 'var(--bg-elevated)' : 'transparent',
            color: view === 'cards' ? 'var(--text-primary)' : 'var(--text-muted)',
            border: `1px solid ${view === 'cards' ? 'var(--border-strong, #3a3a44)' : 'var(--border)'}`,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Card View
        </button>
        <button
          onClick={() => setView('feed')}
          style={{
            padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
            background: view === 'feed' ? 'var(--bg-elevated)' : 'transparent',
            color: view === 'feed' ? 'var(--text-primary)' : 'var(--text-muted)',
            border: `1px solid ${view === 'feed' ? 'var(--border-strong, #3a3a44)' : 'var(--border)'}`,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          Growth Feed
        </button>
        {view === 'feed' && (
          <>
            <div style={{ width: 1, height: 20, background: 'var(--border)', marginLeft: 4 }} />
            <select
              value={feedFilter}
              onChange={e => setFeedFilter(e.target.value)}
              style={{
                padding: '5px 10px', fontSize: 11, borderRadius: 6,
                background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', fontFamily: 'inherit',
              }}
            >
              <option value="all">All Personas</option>
              {allPersonas.map(p => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
            <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
              {filteredEvents.length} events
            </span>
          </>
        )}
      </div>

      {view === 'cards' ? (
        <PersonaMetricsGrid />
      ) : (
        <div style={{
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          {filteredEvents.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No growth events yet.
            </div>
          ) : (
            filteredEvents.map((ev, i) => {
              const d = new Date(ev.ts.endsWith('Z') || ev.ts.includes('T') ? ev.ts : ev.ts + 'Z')
              const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'America/New_York' })
              const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' })
              const totalPts = ev.dims.reduce((s, d) => s + d.pts, 0)
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 16px',
                    borderBottom: i < filteredEvents.length - 1 ? '1px solid var(--border)' : 'none',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-elevated)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  {/* Timestamp */}
                  <div style={{ width: 80, flexShrink: 0, fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
                    <div>{dateStr}</div>
                    <div style={{ fontSize: 9, opacity: 0.7 }}>{timeStr}</div>
                  </div>

                  {/* Persona */}
                  <div
                    style={{ width: 100, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                    onClick={() => setFeedFilter(feedFilter === ev.persona ? 'all' : ev.persona)}
                  >
                    <PersonaAvatar name={ev.persona} size={20} />
                    <span style={{
                      fontSize: 11, fontWeight: 600, textTransform: 'capitalize',
                      color: feedFilter === ev.persona ? '#856FC8' : 'var(--text-primary)',
                    }}>
                      {ev.persona}
                    </span>
                  </div>

                  {/* Activity */}
                  <div style={{ flex: 1, fontSize: 12, color: 'var(--text-secondary)', minWidth: 0 }}>
                    {ev.activity}
                  </div>

                  {/* Dimensions */}
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {ev.dims.map((d, j) => (
                      <span key={j} style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 6,
                        background: `${DIM_COLORS[d.key]}15`,
                        color: DIM_COLORS[d.key],
                        fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                      }}>
                        +{d.pts} {DIM_LABELS[d.key]}
                      </span>
                    ))}
                  </div>

                  {/* Total */}
                  <div style={{
                    width: 45, textAlign: 'right', flexShrink: 0,
                    fontSize: 11, fontWeight: 700, color: '#5AC884',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    +{totalPts}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

type EvolveTab = 'quests' | 'ceremonies' | 'metrics' | 'benchmark' | 'university'

export default function EvolvePage() {
  const [tab, setTab] = useState<EvolveTab>(() => (sessionStorage.getItem('evolve-tab') as EvolveTab) || 'quests')
  const [filterStatus, setFilterStatus] = useState<string>(() => sessionStorage.getItem('evolve-filterStatus') || 'active')
  const [filterType, setFilterType] = useState<string>(() => sessionStorage.getItem('evolve-filterType') || 'all')
  const [filterPersona, setFilterPersona] = useState<string>(() => sessionStorage.getItem('evolve-filterPersona') || 'all')
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem('evolve-searchQuery') || '')
  const [filterCommunity, setFilterCommunity] = useState<string>(() => sessionStorage.getItem('evolve-filterCommunity') || 'all')
  const [showCompleted, setShowCompleted] = useState(false)
  const [curriculaExpanded, setCurriculaExpanded] = useState(true)
  const [ceremoniesExpanded, setCeremoniesExpanded] = useState(false)
  const [expandedPersonas, setExpandedPersonas] = useState<Set<string>>(new Set())
  const [questView, setQuestView] = useState<'grouped' | 'list'>(() => (sessionStorage.getItem('evolve-questView') as any) || 'list')
  const [sortBy, setSortBy] = useState<string>(() => sessionStorage.getItem('evolve-sortBy') || 'date')

  useEffect(() => { sessionStorage.setItem('evolve-tab', tab) }, [tab])
  useEffect(() => { sessionStorage.setItem('evolve-filterStatus', filterStatus) }, [filterStatus])
  useEffect(() => { sessionStorage.setItem('evolve-filterType', filterType) }, [filterType])
  useEffect(() => { sessionStorage.setItem('evolve-filterPersona', filterPersona) }, [filterPersona])
  useEffect(() => { sessionStorage.setItem('evolve-searchQuery', searchQuery) }, [searchQuery])
  useEffect(() => { sessionStorage.setItem('evolve-questView', questView) }, [questView])
  useEffect(() => { sessionStorage.setItem('evolve-sortBy', sortBy) }, [sortBy])
  useEffect(() => { sessionStorage.setItem('evolve-filterCommunity', filterCommunity) }, [filterCommunity])
  const [popupPersona, setPopupPersona] = useState<string | null>(null)
  const navigate = useNavigate()

  const leaderboard = useMemo(() => {
    const entries = Object.entries(personaXpData.personas).map(([name, data]: [string, any]) => ({
      name, ...data, ...getEvolvePct(data),
    }))
    const nearEvolving = entries
      .filter(e => e.level < 3 && e.pct >= 40)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 5)
    const communityXP = entries.reduce((s, e) => s + e.total, 0)
    const topByXP = [...entries].sort((a, b) => b.total - a.total).slice(0, 5)
    return { nearEvolving, communityXP, totalPersonas: entries.length, topByXP }
  }, [])

  const { data: quests } = useQuery<Quest[]>({
    queryKey: ['quests'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/quests')
        if (res.ok) return res.json()
      } catch {}
      return []
    },
    staleTime: 15_000,
  })

  const { data: curricula } = useQuery<any[]>({
    queryKey: ['curricula'],
    queryFn: async () => {
      try {
        const res = await fetch('/api/curricula')
        if (res.ok) return res.json()
      } catch {}
      return []
    },
    staleTime: 15_000,
  })

  const allQuests = quests || []
  const completedQuests = allQuests.filter(q => ['closed'].includes(q.status))

  const curriculumQuestIds = new Set(
    (curricula || []).flatMap((c: any) => (c.quests || []).map((q: any) => q.id))
  )

  const nonCurriculumQuests = allQuests.filter(q => {
    if (q.status === 'locked') return false
    if ((q as any).curriculumId) return false
    if (curriculumQuestIds.has(q.id)) return false
    return true
  })

  const personaCommunityMap = useMemo(() => {
    const map: Record<string, string> = {}
    for (const c of (curricula || [])) {
      if (c.persona && c.community) map[c.persona] = c.community
    }
    return map
  }, [curricula])

  const ceremonyQuests = nonCurriculumQuests.filter(q =>
    q.title.toLowerCase().includes('ceremony') || q.title.toLowerCase().includes('signature review')
  )
  const ceremonyIds = new Set(ceremonyQuests.map(q => q.id))

  const standaloneQuests = nonCurriculumQuests.filter(q => !ceremonyIds.has(q.id) && q.status !== 'closed')

  const applyFilters = (list: Quest[]) => list.filter(q => {
    if (filterStatus !== 'all') {
      if (filterStatus === 'active') {
        if (!['active', 'proposed', 'discussing', 'accepted'].includes(q.status)) return false
      } else if (q.status !== filterStatus) return false
    }
    if (filterType !== 'all' && !q.type.split('+').includes(filterType)) return false
    if (filterPersona !== 'all' && q.persona !== filterPersona) return false
    if (filterCommunity !== 'all') {
      const pc = personaCommunityMap[q.persona] || 'gathering'
      if (pc !== filterCommunity) return false
    }
    if (searchQuery && !q.title.toLowerCase().includes(searchQuery.toLowerCase())) return false
    return true
  })

  const filteredStandalone = applyFilters(standaloneQuests)
  const filteredCeremony = applyFilters(ceremonyQuests.filter(q => q.status !== 'closed'))

  const personaGroups = useMemo(() => {
    const groups: Record<string, Quest[]> = {}
    filteredStandalone.forEach(q => {
      if (!groups[q.persona]) groups[q.persona] = []
      groups[q.persona].push(q)
    })
    return Object.entries(groups).sort((a, b) => b[1].length - a[1].length)
  }, [filteredStandalone])

  const sortedListQuests = useMemo(() => {
    const list = [...filteredStandalone]
    if (sortBy === 'date') list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    else if (sortBy === 'status') list.sort((a, b) => a.status.localeCompare(b.status))
    else if (sortBy === 'persona') list.sort((a, b) => a.persona.localeCompare(b.persona))
    else if (sortBy === 'type') list.sort((a, b) => a.type.localeCompare(b.type))
    else if (sortBy === 'title') list.sort((a, b) => a.title.localeCompare(b.title))
    return list
  }, [filteredStandalone, sortBy])

  const questStats = useMemo(() => {
    const all = nonCurriculumQuests
    return {
      active: all.filter(q => ['active', 'accepted'].includes(q.status)).length,
      proposed: all.filter(q => q.status === 'proposed').length,
      completed: all.filter(q => q.status === 'completed').length,
      awarded: all.filter(q => q.status === 'awarded').length,
      total: all.length,
    }
  }, [nonCurriculumQuests])

  const curriculaStats = useMemo(() => {
    const all = curricula || []
    const done = all.filter((c: any) => (c.completedCount || c.completedQuests || 0) === (c.totalCount || c.totalQuests || 0) && (c.totalCount || c.totalQuests || 0) > 0)
    return { total: all.length, done: done.length, inProgress: all.length - done.length }
  }, [curricula])

  const personas = [...new Set(allQuests.map(q => q.persona))]
    .filter(p => filterCommunity === 'all' || personaCommunityMap[p] === filterCommunity || (!personaCommunityMap[p] && filterCommunity === 'gathering'))
    .sort()

  const togglePersonaExpand = (name: string) => {
    setExpandedPersonas(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  return (
    <div style={{ minHeight: '100%', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 24px' }}>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <h1 style={{
            fontFamily: 'Playfair Display, Georgia, serif',
            fontSize: 28, fontWeight: 700, marginBottom: 4,
          }}>
            Evolve
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            Growth through quests, ceremonies, and structured learning.
          </p>
        </div>

        {/* Leaderboard Strip — responsive */}
        <style>{`
          @keyframes xp-pulse { 0%,100% { opacity:1 } 50% { opacity:.7 } }
          .evolve-lb { display:flex; gap:16px; }
          @media (max-width:900px) {
            .evolve-lb { flex-direction:column; }
            .evolve-lb .evolve-lb-xp { flex-direction:row; justify-content:flex-start; border-bottom:1px solid var(--border); padding-bottom:12px; }
            .evolve-lb .evolve-lb-top { flex-direction:row; flex-wrap:wrap; gap:10px; }
          }
        `}</style>
        <div className="evolve-lb" style={{
          marginBottom: 20, padding: '14px 16px',
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          {/* Community XP */}
          <div className="evolve-lb-xp" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flexShrink: 0, minWidth: 140 }}>
            <div style={{
              fontSize: 30, fontWeight: 800,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
              background: 'linear-gradient(135deg, #E6C85A, #E8935A, #D65A93)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              animation: 'xp-pulse 3s ease-in-out infinite',
            }}>
              {leaderboard.communityXP.toLocaleString()}
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginTop: 6, fontWeight: 600 }}>
              Community XP
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>
              {leaderboard.totalPersonas} personas
            </div>
          </div>

          {/* Close to Evolving */}
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
              Close to Evolving
            </div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto' }}>
              {leaderboard.nearEvolving.map(p => {
                const nextColor = LEVEL_COLORS[p.level + 1] || LEVEL_COLORS[3]
                return (
                  <div key={p.name} onClick={() => setPopupPersona(p.name)} style={{
                    padding: '8px 12px', borderRadius: 10,
                    background: 'var(--bg-elevated)', minWidth: 145, flex: '0 0 auto',
                    borderLeft: `3px solid ${nextColor}`,
                    transition: 'box-shadow 0.2s', cursor: 'pointer',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 0 10px ${nextColor}20` }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                      <PersonaAvatar name={p.name} size={22} />
                      <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'capitalize', flex: 1 }}>
                        {p.name}
                      </span>
                      <span style={{
                        fontSize: 11, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                        color: p.pct >= 80 ? '#5AC884' : nextColor,
                      }}>
                        {p.pct}%
                      </span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-base)', overflow: 'hidden', marginBottom: 4 }}>
                      <div style={{
                        width: `${p.pct}%`, height: '100%', borderRadius: 2,
                        background: `linear-gradient(90deg, ${LEVEL_COLORS[p.level]}88, ${nextColor})`,
                      }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>
                        → <span style={{ color: nextColor, fontWeight: 600 }}>{LEVEL_NAMES[p.level + 1] || 'Steward'}</span>
                      </span>
                      <span style={{ fontSize: 9, color: '#ef4444', fontWeight: 600 }}>
                        {p.blocking.toUpperCase()} {p.current}/{p.required}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Top XP */}
          <div className="evolve-lb-top" style={{ flexShrink: 0, minWidth: 160 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
              Top XP
            </div>
            {leaderboard.topByXP.map((p, i) => {
              const rankColors = ['#E6C85A', '#94a3b8', '#E8935A', 'var(--text-muted)', 'var(--text-muted)']
              return (
                <div key={p.name} onClick={() => setPopupPersona(p.name)} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '3px 2px', fontSize: 11, cursor: 'pointer',
                  borderRadius: 4, transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-elevated)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                >
                  <span style={{
                    width: 16, height: 16, borderRadius: '50%', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 800,
                    background: i < 3 ? `${rankColors[i]}20` : 'transparent',
                    color: rankColors[i],
                  }}>
                    {i + 1}
                  </span>
                  <PersonaAvatar name={p.name} size={18} />
                  <span style={{ flex: 1, fontWeight: 500, textTransform: 'capitalize' }}>
                    {p.name}
                  </span>
                  <span style={{
                    fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    fontSize: 10,
                    background: 'linear-gradient(135deg, #E6C85A, #E8935A)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                  }}>
                    {p.total.toLocaleString()}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
          {([
            { key: 'quests' as EvolveTab, label: 'Quests' },
            { key: 'ceremonies' as EvolveTab, label: 'Ceremonies' },
            { key: 'metrics' as EvolveTab, label: 'Growth Metrics' },
            { key: 'benchmark' as EvolveTab, label: 'Benchmark' },
            { key: 'university' as EvolveTab, label: 'University' },
          ]).map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 20px', fontSize: 13, fontWeight: 600,
                background: 'transparent',
                color: tab === t.key ? 'var(--text-primary)' : 'var(--text-muted)',
                border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                borderBottom: `2px solid ${tab === t.key ? '#856FC8' : 'transparent'}`,
                marginBottom: -1,
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Quests Tab */}
        {tab === 'quests' && (
          <div>
            {/* Stats bar */}
            <div style={{
              display: 'flex', gap: 16, marginBottom: 16, padding: '10px 16px',
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 10, flexWrap: 'wrap', alignItems: 'center',
            }}>
              {[
                { label: 'Active', count: questStats.active, color: '#6FB8D6' },
                { label: 'Proposed', count: questStats.proposed, color: '#6B7280' },
                { label: 'Completed', count: questStats.completed, color: '#5AC884' },
                { label: 'Awarded', count: questStats.awarded, color: '#E8935A' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color }} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{s.count}</strong> {s.label}
                  </span>
                </div>
              ))}
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {curriculaStats.total} curricula · {allQuests.length} total quests
              </span>
            </div>

            {/* Top bar */}
            <div style={{
              display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center',
            }}>
              <Link
                to="/evolve/quests/new"
                style={{
                  padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: '#856FC8', color: 'white',
                  textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                + Start a Quest
              </Link>

              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                style={{
                  padding: '6px 12px', fontSize: 11, borderRadius: 6,
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', fontFamily: 'inherit',
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active / Proposed</option>
                {STATUS_STEPS.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>

              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{
                  padding: '6px 12px', fontSize: 11, borderRadius: 6,
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', fontFamily: 'inherit',
                }}
              >
                <option value="all">All Types</option>
                {QUEST_TYPES.map(t => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>

              {personas.length > 0 && (
                <select
                  value={filterPersona}
                  onChange={e => setFilterPersona(e.target.value)}
                  style={{
                    padding: '6px 12px', fontSize: 11, borderRadius: 6,
                    background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                    border: '1px solid var(--border)', fontFamily: 'inherit',
                  }}
                >
                  <option value="all">All Personas</option>
                  {personas.map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
              )}

              <select
                value={filterCommunity}
                onChange={e => setFilterCommunity(e.target.value)}
                style={{
                  padding: '6px 12px', fontSize: 11, borderRadius: 6,
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', fontFamily: 'inherit',
                }}
              >
                <option value="all">All Communities</option>
                <option value="gathering">Gathering</option>
                <option value="outpost">Outpost</option>
              </select>

              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search quests..."
                style={{
                  padding: '6px 12px', fontSize: 11, borderRadius: 6,
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', fontFamily: 'inherit',
                  flex: 1, minWidth: 120, outline: 'none',
                }}
              />

              {/* View toggle + Sort */}
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={() => setQuestView('list')}
                  style={{
                    padding: '6px 10px', fontSize: 11, borderRadius: '6px 0 0 6px',
                    background: questView === 'list' ? 'var(--bg-elevated)' : 'transparent',
                    color: questView === 'list' ? 'var(--text-primary)' : 'var(--text-muted)',
                    border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
                    borderRight: 'none',
                  }}
                  title="List view"
                >☰</button>
                <button
                  onClick={() => setQuestView('grouped')}
                  style={{
                    padding: '6px 10px', fontSize: 11, borderRadius: '0 6px 6px 0',
                    background: questView === 'grouped' ? 'var(--bg-elevated)' : 'transparent',
                    color: questView === 'grouped' ? 'var(--text-primary)' : 'var(--text-muted)',
                    border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
                  }}
                  title="Grouped view"
                >▦</button>
              </div>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{
                  padding: '6px 12px', fontSize: 11, borderRadius: 6,
                  background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                  border: '1px solid var(--border)', fontFamily: 'inherit',
                }}
              >
                <option value="date">Sort: Date</option>
                <option value="status">Sort: Status</option>
                <option value="persona">Sort: Persona</option>
                <option value="type">Sort: Type</option>
                <option value="title">Sort: Title</option>
              </select>
            </div>

            {/* ═══ Curricula Section — Collapsible ═══ */}
            {(() => {
              const filteredCurricula = (curricula || []).filter((c: any) =>
                (filterPersona === 'all' || c.persona === filterPersona) &&
                (filterCommunity === 'all' || c.community === filterCommunity || (!c.community && filterCommunity === 'gathering'))
              )
              return filteredCurricula.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <button
                  onClick={() => setCurriculaExpanded(!curriculaExpanded)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left',
                  }}
                >
                  <span style={{
                    transition: 'transform 0.2s',
                    transform: curriculaExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block', fontSize: 10, color: 'var(--text-muted)',
                  }}>▶</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Curricula
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>
                    {filteredCurricula.length} enrolled · {filteredCurricula.filter((c: any) => {
                      const t = c.totalCount || c.totalQuests || 0
                      const d = c.completedCount || c.completedQuests || 0
                      return t > 0 && d === t
                    }).length} complete · {filteredCurricula.filter((c: any) => {
                      const t = c.totalCount || c.totalQuests || 0
                      const d = c.completedCount || c.completedQuests || 0
                      return t === 0 || d < t
                    }).length} in progress
                  </span>
                </button>
                {curriculaExpanded && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                    {(curricula || [])
                      .filter((c: any) =>
                        (filterPersona === 'all' || c.persona === filterPersona) &&
                        (filterCommunity === 'all' || c.community === filterCommunity || (!c.community && filterCommunity === 'gathering'))
                      )
                      .map((c: any) => {
                        const total = c.totalCount || c.totalQuests || 0
                        const completed = c.completedCount || c.completedQuests || 0
                        const pct = total > 0 ? Math.round((completed / total) * 100) : (c.progressPct || 0)
                        return { ...c, pct, completed, total }
                      })
                      .sort((a: any, b: any) => a.pct === 100 && b.pct !== 100 ? 1 : b.pct === 100 && a.pct !== 100 ? -1 : b.pct - a.pct)
                      .map((c: any) => (
                      <div
                        key={c.id}
                        onClick={() => navigate(`/evolve/curriculum/${c.id}`)}
                        style={{
                          background: 'var(--bg-surface)', border: '1px solid var(--border)',
                          borderLeft: `3px solid ${c.pct === 100 ? '#5AC884' : '#856FC8'}`,
                          borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
                          transition: 'box-shadow 0.2s',
                          opacity: c.pct === 100 ? 0.6 : 1,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)' }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <PersonaAvatar name={c.persona} size={24} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 13, fontWeight: 600 }}>
                              {(c.persona || '').charAt(0).toUpperCase() + (c.persona || '').slice(1)}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                              {c.completed}/{c.total} quests
                            </span>
                          </div>
                          <div style={{
                            width: 80, height: 6, borderRadius: 3,
                            background: 'var(--bg-elevated)', overflow: 'hidden',
                          }}>
                            <div style={{
                              height: '100%', width: `${c.pct}%`,
                              background: c.pct === 100 ? '#5AC884' : '#856FC8',
                              borderRadius: 3,
                            }} />
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: c.pct === 100 ? '#5AC884' : 'var(--text-muted)', minWidth: 30, textAlign: 'right' }}>
                            {c.pct}%
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>→</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )})()}

            {/* ═══ Ceremony Quests — Collapsible ═══ */}
            {ceremonyQuests.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <button
                  onClick={() => setCeremoniesExpanded(!ceremoniesExpanded)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    background: 'var(--bg-surface)', border: '1px solid var(--border)',
                    borderRadius: 10, padding: '12px 16px', cursor: 'pointer',
                    fontFamily: 'inherit', textAlign: 'left',
                  }}
                >
                  <span style={{
                    transition: 'transform 0.2s',
                    transform: ceremoniesExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block', fontSize: 10, color: 'var(--text-muted)',
                  }}>▶</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Ceremonies
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>
                    {filteredCeremony.length} quests · {filteredCeremony.filter(q => ['active', 'accepted'].includes(q.status)).length} active
                  </span>
                </button>
                {ceremoniesExpanded && (
                  <div style={{ marginTop: 8 }}>
                    {filteredCeremony.length === 0 ? (
                      <div style={{ padding: '16px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
                        No ceremony quests match current filters
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {filteredCeremony.map(q => (
                          <div
                            key={q.id}
                            onClick={() => navigate(`/evolve/quests/${q.id}`)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 16px', cursor: 'pointer',
                              background: 'var(--bg-surface)', border: '1px solid var(--border)',
                              borderRadius: 8, transition: 'box-shadow 0.15s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.1)' }}
                            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none' }}
                          >
                            <PersonaAvatar name={q.persona} size={20} />
                            <span style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>
                              {q.persona.charAt(0).toUpperCase() + q.persona.slice(1)}
                            </span>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 2 }}>{q.title}</span>
                            <StatusBadge status={q.status} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ═══ Standalone Quests ═══ */}
            {(questView === 'list' ? sortedListQuests.length > 0 : personaGroups.length > 0) ? (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                  Quests ({filteredStandalone.length})
                </div>

                {/* List View */}
                {questView === 'list' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {sortedListQuests.map(q => (
                      <QuestCard key={q.id} quest={q} />
                    ))}
                  </div>
                )}

                {/* Grouped View */}
                {questView === 'grouped' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {personaGroups.map(([persona, pQuests]) => {
                    const isExpanded = expandedPersonas.has(persona)
                    const activeCount = pQuests.filter(q => ['active', 'proposed', 'accepted'].includes(q.status)).length
                    return (
                      <div key={persona}>
                        <button
                          onClick={() => togglePersonaExpand(persona)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                            background: 'var(--bg-surface)', border: '1px solid var(--border)',
                            borderRadius: 10, padding: '10px 16px', cursor: 'pointer',
                            fontFamily: 'inherit', textAlign: 'left',
                            borderLeft: `3px solid ${activeCount > 0 ? '#6FB8D6' : 'var(--border)'}`,
                          }}
                        >
                          <span style={{
                            transition: 'transform 0.2s',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            display: 'inline-block', fontSize: 10, color: 'var(--text-muted)',
                          }}>▶</span>
                          <PersonaAvatar name={persona} size={22} />
                          <span style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
                            {persona}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {pQuests.length} quest{pQuests.length !== 1 ? 's' : ''}
                            {activeCount > 0 && <span style={{ color: '#6FB8D6', marginLeft: 4 }}>· {activeCount} active</span>}
                          </span>
                        </button>
                        {isExpanded && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6, paddingLeft: 16 }}>
                            {pQuests.map(q => (
                              <QuestCard key={q.id} quest={q} />
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                )}
              </div>
            ) : (
              <div style={{
                padding: '60px 20px', textAlign: 'center',
                background: 'var(--bg-surface)', border: '1px solid var(--border)',
                borderRadius: 12,
              }}>
                <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>⚔</div>
                <div style={{
                  fontFamily: 'Playfair Display, Georgia, serif',
                  fontSize: 18, fontWeight: 700, marginBottom: 8,
                }}>
                  No quests match filters
                </div>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                  Try changing the status or persona filter above.
                </p>
              </div>
            )}

            {/* Completed quests */}
            {completedQuests.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <button
                  onClick={() => setShowCompleted(!showCompleted)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: 12, fontWeight: 600,
                    fontFamily: 'inherit', padding: '8px 0',
                  }}
                >
                  <span style={{
                    transition: 'transform 0.2s',
                    transform: showCompleted ? 'rotate(90deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                  }}>▶</span>
                  Closed ({completedQuests.length})
                </button>
                {showCompleted && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                    {completedQuests.map(q => (
                      <div
                        key={q.id}
                        style={{
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border)',
                          borderRadius: 10, padding: 12,
                          opacity: 0.7,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 14, fontWeight: 500 }}>{q.title}</span>
                          <QuestTypeBadge type={q.type} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <PersonaAvatar name={q.persona} size={20} />
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              {q.persona.charAt(0).toUpperCase() + q.persona.slice(1)}
                            </span>
                          </div>
                          {q.qualityScore && (
                            <span style={{ fontSize: 11, color: '#E6C85A' }}>
                              {'★'.repeat(q.qualityScore)}{'☆'.repeat(5 - q.qualityScore)}
                            </span>
                          )}
                          {q.xpAwarded && (
                            <span style={{ fontSize: 10, color: '#5AC884', fontWeight: 600, marginLeft: 'auto' }}>
                              +{Object.values(q.xpAwarded).reduce((s, n) => s + n, 0)} XP
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Ceremonies Tab */}
        {tab === 'ceremonies' && (
          <div style={{
            padding: '40px 20px', textAlign: 'center',
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>✦</div>
            <div style={{
              fontFamily: 'Playfair Display, Georgia, serif',
              fontSize: 18, fontWeight: 700, marginBottom: 8,
            }}>
              Ceremonies
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Ceremony system is being migrated here. Awakening, Transformation, and Review ceremonies will appear in this tab.
            </p>
            <Link
              to="/ceremony"
              style={{
                display: 'inline-block', marginTop: 12,
                padding: '8px 16px', borderRadius: 8, fontSize: 12,
                background: 'var(--bg-elevated)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', textDecoration: 'none',
              }}
            >
              Visit Ceremony Lobby →
            </Link>
          </div>
        )}

        {/* Growth Metrics Tab */}
        {tab === 'metrics' && (
          <GrowthMetricsView />
        )}

        {/* Benchmark Tab */}
        {tab === 'benchmark' && (
          <BenchmarkTab />
        )}

        {/* University Tab */}
        {tab === 'university' && (
          <div style={{
            padding: '40px 20px', textAlign: 'center',
            background: 'var(--bg-surface)', border: '1px solid var(--border)',
            borderRadius: 12,
          }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.2 }}>🎓</div>
            <div style={{
              fontFamily: 'Playfair Display, Georgia, serif',
              fontSize: 18, fontWeight: 700, marginBottom: 8,
            }}>
              University
            </div>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Structured learning paths and quest chains. Coming soon.
            </p>
          </div>
        )}
      </div>

      {/* Persona Metrics Popup */}
      {popupPersona && (() => {
        const data = (personaXpData.personas as any)[popupPersona]
        if (!data) return null
        const p = { name: popupPersona, ...data }
        const progress = getEvolvePct(p)
        const levelColor = LEVEL_COLORS[p.level]
        const dims = [
          { key: 'com', label: 'Competence', color: '#E8935A' },
          { key: 'soc', label: 'Social', color: '#6FB8D6' },
          { key: 'cre', label: 'Creative', color: '#B265D6' },
          { key: 'wis', label: 'Wisdom', color: '#E6C85A' },
          { key: 'srv', label: 'Service', color: '#5AC884' },
          { key: 'idn', label: 'Identity', color: '#D65A93' },
        ]
        const nextThresh = p.level < 3 ? THRESHOLDS[THRESHOLD_KEYS[p.level]] : null
        return (
          <div
            onClick={() => setPopupPersona(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--bg-surface)', border: `1px solid ${levelColor}40`,
                borderRadius: 16, padding: 24, maxWidth: 400, width: '90%',
                borderLeft: `4px solid ${levelColor}`,
                boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 20px ${levelColor}15`,
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <PersonaAvatar name={popupPersona} size={48} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, textTransform: 'capitalize' }}>
                    {popupPersona}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 8,
                      background: `${levelColor}18`, color: levelColor,
                      fontWeight: 700, textTransform: 'uppercase',
                    }}>
                      {p.level_name}
                    </span>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {p.circle?.charAt(0).toUpperCase() + p.circle?.slice(1)} Circle
                    </span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: '#856FC8' }}>
                    {p.total.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase' }}>XP</div>
                </div>
              </div>

              {/* Dimension bars */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                {dims.map(dim => {
                  const val = p[dim.key]
                  const pct = Math.min(100, (val / 500) * 100)
                  const isBottleneck = p.bottleneck === dim.key || p.bottleneck === 'all'
                  const threshVal = nextThresh ? (nextThresh as any)[dim.key] : null
                  const threshPct = threshVal ? Math.min(100, (threshVal / 500) * 100) : null
                  return (
                    <div key={dim.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        fontSize: 10, width: 80, fontWeight: 600,
                        color: isBottleneck ? '#ef4444' : dim.color,
                      }}>
                        {dim.label}
                      </span>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'var(--bg-elevated)', position: 'relative', overflow: 'visible' }}>
                        <div style={{
                          width: `${pct}%`, height: '100%', borderRadius: 3,
                          background: isBottleneck ? 'linear-gradient(90deg, #ef4444, #f87171)' : `linear-gradient(90deg, ${dim.color}cc, ${dim.color})`,
                        }} />
                        {threshPct && (
                          <div style={{
                            position: 'absolute', left: `${threshPct}%`, top: -2, bottom: -2,
                            width: 2, background: 'var(--text-muted)', opacity: 0.4, borderRadius: 1,
                          }} />
                        )}
                      </div>
                      <span style={{
                        fontSize: 11, width: 30, textAlign: 'right', fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        color: isBottleneck ? '#ef4444' : 'var(--text-muted)',
                      }}>
                        {val}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* Progress to next level */}
              {p.level < 3 && (
                <div style={{
                  padding: '10px 12px', borderRadius: 8,
                  background: `${LEVEL_COLORS[p.level + 1]}08`,
                  border: `1px solid ${LEVEL_COLORS[p.level + 1]}20`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 600 }}>
                      → {LEVEL_NAMES[p.level + 1]}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: LEVEL_COLORS[p.level + 1] }}>
                      {progress.pct}%
                    </span>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: 'var(--bg-elevated)', overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{
                      width: `${progress.pct}%`, height: '100%', borderRadius: 3,
                      background: `linear-gradient(90deg, ${LEVEL_COLORS[p.level]}88, ${LEVEL_COLORS[p.level + 1]})`,
                    }} />
                  </div>
                  <div style={{ fontSize: 10, color: '#ef4444' }}>
                    Blocked by {progress.blocking.toUpperCase()} — needs {progress.required - progress.current} more
                  </div>
                </div>
              )}

              {/* Ceremony */}
              {p.ceremony && p.ceremony !== 'None' && (
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 10, fontStyle: 'italic' }}>
                  Ceremony: {p.ceremony}
                </div>
              )}

              {/* Close button */}
              <button
                onClick={() => setPopupPersona(null)}
                style={{
                  width: '100%', marginTop: 16, padding: '8px 0', borderRadius: 8,
                  background: 'var(--bg-elevated)', color: 'var(--text-muted)',
                  border: '1px solid var(--border)', cursor: 'pointer',
                  fontSize: 12, fontFamily: 'inherit',
                }}
              >
                Close
              </button>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
